/**
 * IcgStateExplainer — explainer for ICG-Al, the STATE Immunity Closure
 * Guarantee (governance name: STATE Safety Envelope).
 *
 * ICG-Al pairs the INV.CONSISTENCY Dual Pair Guarantee (primal) with the
 * ALWAYS Structural Duality Principle (dual) along the F-algebra ⊣
 * F-coalgebra adjunction. The joint object is a bialgebra: its
 * establishment and preservation structure maps, sealed by the
 * compatibility law, are the two-sided certificate.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AuditSurfaceDelta,
  CalloutBox,
  CounterModelGrid,
  ExplainerCard,
  SafetyEnvelopeDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  THEOREM_COLOR,
} from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const PRIMAL_ACCENT = DUAL_PAIR_COLOR["INV.CONSISTENCY"].color;
const DUAL_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color;

const COUNTER_MODELS = [
  {
    title: "Primal only — INV.CONSISTENCY, no ALWAYS",
    body: "Establishment and preservation cohere inside one regime, but stacking two state disciplines across a refactor is unwitnessed: nothing certifies the joint state machine is itself an INV.CONSISTENCY witness rather than a weaker one.",
  },
  {
    title: "Dual only — ALWAYS, no INV.CONSISTENCY",
    body: "The algebra ↔ coalgebra duality composes, but with no consistency neutralization there is no invariant discipline to compose — ALWAYS certifies the stacking of a state guarantee that was never established.",
  },
] as const;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-icg-al",
  kind: "SSM · Immunity Closure Guarantee (S1⊗S2)",
  title: "ICG-Al",
  subtitle: "STATE Safety Envelope · INV.CONSISTENCY ⊗ ALWAYS",
  accent: PRIMAL_ACCENT,
  accentAlt: DUAL_ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "INV.CONSISTENCY (primal DPG)",
    href: "#explainer-inv-consistency",
    accent: PRIMAL_ACCENT,
  },
  { label: "ALWAYS (dual SDP)", href: "#explainer-always", accent: DUAL_ACCENT },
  {
    label: "INV.ESTABLISH (I1)",
    href: "#explainer-inv-establish",
    accent: THEOREM_COLOR["INV.ESTABLISH"],
  },
  {
    label: "INV.PRESERVE (I2)",
    href: "#explainer-inv-preserve",
    accent: THEOREM_COLOR["INV.PRESERVE"],
  },
];

const ENVELOPE = (
  <SafetyEnvelopeDiagram
    icg="ICG-Al"
    primalLabel="INV.CONSISTENCY"
    primalKind="Dual Pair Guarantee · I1 + I2"
    primalAccent={PRIMAL_ACCENT}
    dualLabel="ALWAYS"
    dualKind="Structural Duality Principle · Al"
    dualAccent={DUAL_ACCENT}
    adjunction="F-algebra ⊣ F-coalgebra"
    unitRole="establishment witness"
    counitRole="preservation receipt"
  />
);

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ICG-Al</strong> is the STATE Safety Envelope. It seals invariant neutralization
      (INV.CONSISTENCY) and its compositional dual (ALWAYS) into one two-sided certificate, so a
      state-discipline audit is a single check rather than two.
    </p>
  ),
  intuition: (
    <>
      <p>
        INV.CONSISTENCY makes "an invariant established but not preservable" — and "a state
        preserved but never established" — unconstructable. ALWAYS certifies that the algebra ↔
        coalgebra duality preserves that guarantee when state disciplines are stacked.
      </p>
      <p>
        Deployed separately they are two artifacts: one says the invariant cannot be malformed, the
        other says composition keeps it well-formed. Nothing forces the two to describe the same
        state machine — the coherence binding them is a paragraph, and a paragraph is where the
        wrong-envelope deputy lives.
      </p>
      <p>
        ICG-Al is the single object — a bialgebra — that contains both sides. Its establishment and
        preservation structure maps, sealed by the compatibility law, are the structural proof that
        the constructor and the observer are governed by one algebraic structure. The envelope is{" "}
        <strong>strictly stronger</strong> than holding the two theorems side by side.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ICG-Al is the bialgebra carrying the adjunction{" "}
        <MathInline>{tex`\text{F-algebra} \dashv \text{F-coalgebra}`}</MathInline>. It is
        simultaneously an F-algebra (establishment) and an F-coalgebra (preservation), and its two
        structural witnesses are:
      </p>
      <ul>
        <li>
          the <strong>unit η</strong> — the WITNESS phase: an establishment witness produced before
          the state is relied upon;
        </li>
        <li>
          the <strong>counit ε</strong> — the ACCOUNT phase: the preservation receipt discharging
          each observation against the established state.
        </li>
      </ul>
      <p>
        Here the triangle identities take the form of the bialgebra <em>compatibility law</em>: it
        forces the algebra and coalgebra structure maps to agree, so establishment and preservation
        cannot drift apart and a coalgebraic "undo" of the constructor is ill-typed.
      </p>
      <AuditSurfaceDelta
        primalCheck="INV.CONSISTENCY witness"
        dualCheck="ALWAYS witness"
        envelopeLabel="ICG-Al envelope"
        primalAccent={PRIMAL_ACCENT}
        dualAccent={DUAL_ACCENT}
      />
      <p style={{ marginTop: "0.5rem" }}>
        Categorical form: ICG-Al is the <em>Adjunction Certificate</em> whose two sides are the
        primal DPG and the dual SDP — the bialgebra compatibility law is the certificate that they
        are mutual sections.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Each side alone admits a counter-model the
        envelope rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="ICG-Al counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        ICG-Al makes the forbidden generator <strong>G3 (time reversal)</strong> structurally
        absent: the compatibility law makes a rollback past an invariant's own establishment
        unconstructable. The envelope enforces security axiom A3, Phase Separation.
      </p>
      <p>
        <strong>Worked adversary.</strong> An actor establishes a security invariant, then uses a
        rollback transition to revoke the state the invariant was built on — leaving downstream code
        trusting an invariant whose foundation no longer exists. Under ICG-Al the establish map and
        the preserve map are tied by the compatibility law, so a transition that "undoes" the
        constructor does not type: the retroactive-invariant attack is structurally inexpressible.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing ICG-Al means bypassing INV.CONSISTENCY
        and ALWAYS at once, or escaping the bialgebra carrier — both leave the kernel category and
        vacate the guarantee rather than violate it.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-Al" accent={DUAL_ACCENT}>
        INV.CONSISTENCY (DPG) ⊗ ALWAYS (SDP) = ICG-Al. The audit surface collapses from a DPG
        witness plus an SDP witness to one envelope carrying the algebra ⊣ coalgebra unit and
        counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Immunity Closure Guarantees (§7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "RD-97 — Immunity Closure Guarantees",
      path: "research/settled/RD-97-BI-GUARANTEES-TWO-SIDED-SAFETY.md",
      note: "Canonical source for the seven-envelope algebra and the naming triad.",
    },
    {
      label: "Safety Envelope foundations",
      path: "theory/structural/Immunity-Closure-Guarantees/00-Safety-Envelope-Foundations.md",
    },
    {
      label: "ICG-Al theory document",
      path: "theory/structural/Immunity-Closure-Guarantees/02-ICG-Al-Invariant-Consistency-And-Always.md",
    },
    {
      label: "Immunity Closure Guarantee (Agda)",
      path: "agda/Research/BiGuarantee/BiInvariance.agda",
    },
  ],
};

export function IcgStateExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={ENVELOPE} crossLinks={CROSS_LINKS} />
  );
}
